import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';

import {
  Card,
  Badge,
  Button,
  Icon,
  List,
  Loading,
  Row,
  Col,
} from '@folio/stripes/components';

import PrinterMonitoringService from './PrinterMonitoringService';

/**
 * Printer Status Monitor Component
 * Displays real-time printer status and monitoring information
 * kware: مكون مراقبة حالة الطابعات مع معلومات فورية
 */
const PrinterStatusMonitor = ({
  printers = [],
  onPrinterTest,
  onPrinterReconnect,
  compact = false
}) => {
  const intl = useIntl();
  const monitoringService = useRef(new PrinterMonitoringService());
  const [printerStatuses, setPrinterStatuses] = useState({});
  const [monitoringStats, setMonitoringStats] = useState({});
  const [selectedPrinterId, setSelectedPrinterId] = useState(null);

  // Initialize monitoring for printers
  useEffect(() => {
    const service = monitoringService.current;

    // Start monitoring for each printer with connection
    printers.forEach(printer => {
      if (printer.connection && !service.monitors.has(printer.id)) {
        service.startMonitoring(printer.id, printer.connection);
      }
    });

    // Stop monitoring for removed printers
    const activePrinters = Array.from(service.monitors.keys());
    const currentPrinterIds = printers.map(p => p.id);

    activePrinters.forEach(printerId => {
      if (!currentPrinterIds.includes(printerId)) {
        service.stopMonitoring(printerId);
      }
    });

    // Update states
    setPrinterStatuses(service.getAllPrinterStatuses());
    setMonitoringStats(service.getMonitoringStats());

    return () => {
      // Cleanup on unmount
      service.cleanup();
    };
  }, [printers]);

  // Listen for status updates
  useEffect(() => {
    const handleStatusUpdate = (event) => {
      const { printerId, status } = event.detail;

      setPrinterStatuses(prev => ({
        ...prev,
        [printerId]: monitoringService.current.getPrinterStatus(printerId)
      }));

      setMonitoringStats(monitoringService.current.getMonitoringStats());
    };

    window.addEventListener('printerStatusUpdate', handleStatusUpdate);

    return () => {
      window.removeEventListener('printerStatusUpdate', handleStatusUpdate);
    };
  }, []);

  /**
   * Get status badge color
   */
  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 'ready': return 'success';
      case 'error': return 'red';
      case 'disconnected': return 'red';
      case 'starting': return 'default';
      default: return 'default';
    }
  };

  /**
   * Get status icon
   */
  const getStatusIcon = (status) => {
    switch (status) {
      case 'ready': return 'check';
      case 'error': return 'exclamation';
      case 'disconnected': return 'times';
      case 'starting': return 'clock';
      default: return 'question';
    }
  };

  /**
   * Format uptime
   */
  const formatUptime = (uptimeMs) => {
    if (!uptimeMs) return 'N/A';

    const seconds = Math.floor(uptimeMs / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);

    if (hours > 0) {
      return `${hours}h ${minutes % 60}m`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds % 60}s`;
    } else {
      return `${seconds}s`;
    }
  };

  /**
   * Handle printer test
   */
  const handlePrinterTest = async (printer) => {
    try {
      const result = await monitoringService.current.testPrinterConnectivity(printer.id);

      if (onPrinterTest) {
        onPrinterTest(printer, result);
      }
    } catch (error) {
      console.error('Printer test failed:', error);
    }
  };

  /**
   * Render compact view
   */
  if (compact) {
    return (
      <div style={{
        display: 'flex',
        gap: '8px',
        alignItems: 'center',
        fontSize: '12px'
      }}
      >
        <Icon icon="desktop" size="small" />
        <span>
          <FormattedMessage
            id="ui-plugin-inventory-templates.printerMonitor.compact"
            defaultMessage="{active}/{total} active"
            values={{
              active: monitoringStats.activePrinters || 0,
              total: monitoringStats.totalPrinters || 0
            }}
          />
        </span>
        {monitoringStats.errorPrinters > 0 && (
          <Badge color="red" style={{ fontSize: '10px' }}>
            {monitoringStats.errorPrinters} errors
          </Badge>
        )}
      </div>
    );
  }

  return (
    <Card>
      <div style={{ padding: '16px' }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '16px'
        }}
        >
          <h4 style={{ margin: 0 }}>
            <FormattedMessage
              id="ui-plugin-inventory-templates.printerMonitor.title"
              defaultMessage="Printer Status Monitor"
            />
          </h4>

          <div style={{ display: 'flex', gap: '8px' }}>
            <Badge color="primary">
              {monitoringStats.totalPrinters || 0} printers
            </Badge>
            {monitoringStats.activePrinters > 0 && (
              <Badge color="success">
                {monitoringStats.activePrinters} active
              </Badge>
            )}
            {monitoringStats.errorPrinters > 0 && (
              <Badge color="red">
                {monitoringStats.errorPrinters} errors
              </Badge>
            )}
          </div>
        </div>

        {/* Printer List */}
        {printers.length === 0 ? (
          <div style={{
            textAlign: 'center',
            color: '#666',
            padding: '20px',
            fontSize: '14px'
          }}
          >
            <Icon icon="desktop" size="large" style={{ marginBottom: '8px' }} />
            <div>
              <FormattedMessage
                id="ui-plugin-inventory-templates.printerMonitor.noPrinters"
                defaultMessage="No printers connected"
              />
            </div>
          </div>
        ) : (
          <List
            items={printers}
            itemFormatter={(printer) => {
              const status = printerStatuses[printer.id];

              return (
                <div
                  key={printer.id}
                  style={{
                    padding: '12px',
                    border: selectedPrinterId === printer.id ? '2px solid #0071bc' : '1px solid #ddd',
                    borderRadius: '4px',
                    marginBottom: '8px',
                    cursor: 'pointer'
                  }}
                  onClick={() => setSelectedPrinterId(
                    selectedPrinterId === printer.id ? null : printer.id
                  )}
                >
                  <Row>
                    <Col xs={8}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Icon
                          icon={getStatusIcon(status?.status)}
                          size="medium"
                          style={{
                            color: getStatusBadgeColor(status?.status) === 'success' ? '#28a745' :
                              getStatusBadgeColor(status?.status) === 'red' ? '#dc3545' : '#6c757d'
                          }}
                        />
                        <div>
                          <div style={{ fontWeight: 'bold', fontSize: '14px' }}>
                            {printer.name}
                          </div>
                          <div style={{ fontSize: '12px', color: '#666' }}>
                            {printer.connectionType} • {printer.type}
                          </div>
                        </div>
                      </div>
                    </Col>

                    <Col xs={4} style={{ textAlign: 'right' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '8px' }}>
                        <Badge color={getStatusBadgeColor(status?.status)}>
                          {status?.status || 'unknown'}
                        </Badge>

                        <Button
                          buttonStyle="default"
                          buttonClass="primary"
                          onClick={(e) => {
                            e.stopPropagation();
                            handlePrinterTest(printer);
                          }}
                          disabled={!status}
                          style={{ fontSize: '12px', padding: '4px 8px' }}
                        >
                          Test
                        </Button>
                      </div>
                    </Col>
                  </Row>

                  {/* Expanded Details */}
                  {selectedPrinterId === printer.id && status && (
                    <div style={{
                      marginTop: '12px',
                      paddingTop: '12px',
                      borderTop: '1px solid #eee',
                      fontSize: '12px'
                    }}
                    >
                      <Row>
                        <Col xs={6}>
                          <div><strong>Last Check:</strong> {
                            status.lastCheck ?
                              new Date(status.lastCheck).toLocaleTimeString('ar') :
                              'Never'
                          }
                          </div>
                          <div><strong>Uptime:</strong> {formatUptime(status.stats?.uptime)}</div>
                          <div><strong>Total Checks:</strong> {status.stats?.totalChecks || 0}</div>
                        </Col>
                        <Col xs={6}>
                          <div><strong>Success Rate:</strong> {
                            status.stats?.totalChecks > 0 ?
                              Math.round((status.stats.successfulChecks / status.stats.totalChecks) * 100) + '%' :
                              'N/A'
                          }
                          </div>
                          <div><strong>Errors:</strong> {status.stats?.errorCount || 0}</div>
                          {status.stats?.lastError && (
                            <div style={{ color: '#dc3545' }}>
                              <strong>Last Error:</strong> {status.stats.lastError.message}
                            </div>
                          )}
                        </Col>
                      </Row>

                      {/* Active Alerts */}
                      {status.alerts && status.alerts.length > 0 && (
                        <div style={{ marginTop: '8px' }}>
                          <strong>Active Alerts:</strong>
                          {status.alerts.map((alert, index) => (
                            <Badge
                              key={index}
                              color={alert.type === 'error' ? 'red' : 'default'}
                              style={{ marginLeft: '4px', fontSize: '10px' }}
                            >
                              {alert.message}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            }}
          />
        )}

        {/* Global Actions */}
        {printers.length > 0 && (
          <div style={{
            marginTop: '16px',
            paddingTop: '16px',
            borderTop: '1px solid #eee',
            display: 'flex',
            gap: '8px',
            justifyContent: 'flex-end'
          }}
          >
            <Button
              buttonStyle="default"
              onClick={() => {
                printers.forEach(printer => {
                  if (printerStatuses[printer.id]) {
                    handlePrinterTest(printer);
                  }
                });
              }}
            >
              <Icon icon="refresh" size="small" style={{ marginRight: '4px' }} />
              <FormattedMessage
                id="ui-plugin-inventory-templates.printerMonitor.testAll"
                defaultMessage="Test All"
              />
            </Button>
          </div>
        )}
      </div>
    </Card>
  );
};

PrinterStatusMonitor.propTypes = {
  printers: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
    connectionType: PropTypes.string.isRequired,
    connection: PropTypes.object
  })),
  onPrinterTest: PropTypes.func,
  onPrinterReconnect: PropTypes.func,
  compact: PropTypes.bool
};

export default PrinterStatusMonitor;
